/**
 * Текст строки выпадающего списка, который не помещается по ширине.
 *
 * В покое обрезается многоточием. Если задержать на нём указатель на
 * секунду, текст начинает медленно прокручиваться туда и обратно,
 * пока указатель не уйдёт. Помещающийся текст не двигается: прокрутка
 * короткой строки только отвлекала бы.
 */

import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";

import { cn } from "./cn";

/** Задержка перед прокруткой: случайный проход указателем её не запускает. */
export const MARQUEE_DELAY_MS = 1000;

/** Скорость, при которой текст успевают прочитать. */
const SPEED_PX_PER_S = 40;

/** Движение занимает 60 % цикла: в начале и в конце текст стоит (см. index.css). */
const MOVING_SHARE = 0.6;

interface OverflowMarqueeProps {
  children: ReactNode;
  className?: string;
}

export function OverflowMarquee({ children, className }: OverflowMarqueeProps) {
  const box = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** На сколько пикселей текст шире строки; null — прокрутки нет. */
  const [overflow, setOverflow] = useState<number | null>(null);

  const cancel = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  // Список закрылся, пока шёл отсчёт, — таймер не должен пережить строку.
  useEffect(() => cancel, []);

  const start = () => {
    cancel();
    timer.current = setTimeout(() => {
      timer.current = null;
      const element = box.current;
      if (element === null) return;
      // Ширину меряем в момент запуска, а не заранее: список могли
      // растянуть, и текст, не помещавшийся раньше, теперь помещается.
      const hidden = element.scrollWidth - element.clientWidth;
      if (hidden > 1) setOverflow(hidden);
    }, MARQUEE_DELAY_MS);
  };

  const stop = () => {
    cancel();
    setOverflow(null);
  };

  const running = overflow !== null;
  const style = running
    ? ({
        "--marquee-shift": `-${overflow}px`,
        "--marquee-duration": `${Math.max(2, overflow / SPEED_PX_PER_S) / MOVING_SHARE}s`,
      } as CSSProperties)
    : undefined;

  return (
    <span
      ref={box}
      data-marquee={running ? "running" : "idle"}
      onMouseEnter={start}
      onMouseLeave={stop}
      className={cn(
        "min-w-0 flex-1 overflow-hidden whitespace-nowrap",
        // Многоточие — только в покое: у движущегося текста оно стояло бы
        // посреди строки.
        running ? "text-clip" : "text-ellipsis",
        className,
      )}
    >
      <span className={running ? "tf-marquee inline-block" : undefined} style={style}>
        {children}
      </span>
    </span>
  );
}
